import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';;
import type {
  BrandResponse,
  CreateBrandRequest,
  UpdateBrandRequest,
  ListBrandsResponse,
  ListBrandsRequest,
  CreateResponseData,
  UpdateResponseData
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class BrandService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL = environment.RETAIL_BILLING_URL ;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });
    
    return httpParams;
  }

  // Company-level brand operations
  getCompanyBrands(request?: ListBrandsRequest): Observable<ListBrandsResponse> {
    // Map the request to API-expected parameters
    const params: any = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10
    };
    
    // Add optional search parameters
    if (request?.search_term) {
      params.SearchTerm = request.search_term;
    }
    if (request?.status !== undefined) {
      params.Status = request.status;
    }
    
    return this.http.get<ListBrandsResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.GET_BRANDS,
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Brands API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getCompanyBrand(brandId: number): Observable<BrandResponse> {
    const params = {
      CompanyId: 50
    };

    return this.http.get<BrandResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.GET_BRAND_BY_ID
        .replace('{brand_id}', brandId.toString()),
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Company Brand API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createCompanyBrand(request: CreateBrandRequest): Observable<CreateResponseData> {
    return this.http.post<CreateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.CREATE_BRAND,
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Company Brand API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateCompanyBrand(brandId: number, request: UpdateBrandRequest): Observable<UpdateResponseData> {
    return this.http.put<UpdateResponseData>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.UPDATE_BRAND
        .replace('{brand_id}', brandId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Company Brand API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}